import React from "react";

import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { FocusScope } from "@radix-ui/react-focus-scope";
import classNames from "classnames";
import { createPortal } from "react-dom";
import { v4 as uuidv4 } from "uuid";

import Button from "../button/Button";
import Tooltip from "../tooltip/Tooltip";

import type { JSX, ReactElement, ReactNode } from "react";

import type { ButtonVariants } from "../button/Button";

export const MODAL_SIZES = {
  SM: "sm",
  MD: "md",
} as const;

type ModalSizeKeys = keyof typeof MODAL_SIZES;

export type ModalSizes = (typeof MODAL_SIZES)[ModalSizeKeys];

export interface ModalActions {
  label: string | JSX.Element;
  id?: string;
  buttonVariant?: ButtonVariants;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  tooltipOptions?: {
    id: string;
    content: string;
  };
  onClick?: () => void;
  isCloseAfterAction?: boolean;
  formId?: string;
}

export interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  actions?: ModalActions[];
  size?: ModalSizes;
  withDarkBackground?: boolean;
  header?: ReactElement | null | string;
  isDivided?: boolean;
  isCloseBackdrop?: boolean;
  id?: string;
  className?: string;
}

function Modal({
  isOpen,
  onClose,
  children,
  actions,
  size,
  withDarkBackground,
  id,
  header,
  isDivided,
  isCloseBackdrop = true,
  className,
}: ModalProps): JSX.Element | null {
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget && onClose && isCloseBackdrop) {
      onClose();
    }
  };

  const hasFooter = actions && actions.length > 0;

  const containerClasseNames = classNames(
    className,
    "flex flex-col rounded-[0.46875rem] bg-card py-[1.25rem] drop-shadow-lg max-h-full max-w-full",
    isDivided && "divide-y",
    size === "sm" && "w-[375px]",
    size === "md" && "w-[600px]",
  );

  const backdropClasseNames = classNames(
    "fixed inset-0 z-[1500] flex items-center justify-center backdrop-blur-sm p-[1.5625rem] pointer-events-auto",
    withDarkBackground ? "bg-overlay" : "bg-white/25 dark:bg-overlay/50",
  );

  const headerClassNames = classNames(
    "flex items-start justify-between px-[1.25rem]",
    isDivided && "pb-[1.25rem]",
  );

  const bodyClassNames = classNames(
    "max-h-full max-w-full overflow-auto flex-1",
    { "p-[1.25rem]": isDivided && hasFooter },
    { "mb-[2.1875rem] mt-[0.625rem] px-[1.25rem]": !isDivided },
    { "px-[1.25rem] pt-[1.25rem]": isDivided && !hasFooter },
  );

  const footerClassNames = classNames(
    "flex justify-end gap-[0.78125rem] px-[1.25rem]",
    isDivided && "pt-[1.25rem]",
  );

  const handleActionClick = ({
    onClick,
    isCloseAfterAction = true,
  }: ModalActions): void => {
    onClick?.();
    if (isCloseAfterAction) {
      onClose?.();
    }
  };

  const renderActions = (): ReactNode =>
    actions?.map((action) => {
      const actionButtonContainerClassNames = classNames({
        "w-full": action.isFullWidth,
      });

      return (
        <div
          key={action.id ?? uuidv4()}
          className={actionButtonContainerClassNames}
        >
          <Button
            data-tooltip-id={action?.tooltipOptions?.id}
            form={action.formId}
            isDisabled={action.isDisabled}
            isFullWidth={action.isFullWidth}
            onClick={(): void => handleActionClick(action)}
            size="md"
            type={action.formId ? "submit" : "button"}
            variant={action.buttonVariant}
          >
            {action.label}
          </Button>
          {action.tooltipOptions ? (
            <Tooltip
              content={action.tooltipOptions.content}
              id={action.tooltipOptions.id}
            />
          ) : null}
        </div>
      );
    });

  return isOpen
    ? createPortal(
        <FocusScope>
          <div
            className={backdropClasseNames}
            id={id}
            onClick={handleBackdropClick}
          >
            <div className={containerClasseNames}>
              <div className={headerClassNames}>
                {header}
                {onClose ? (
                  <button
                    className="ml-auto cursor-pointer"
                    onClick={onClose}
                    type="button"
                  >
                    <Icon color="#98A2B3" path={mdiClose} size={0.9375} />
                  </button>
                ) : null}
              </div>
              <div className={bodyClassNames}>{children}</div>
              {hasFooter ? (
                <div className={footerClassNames}>{renderActions()}</div>
              ) : null}
            </div>
          </div>
        </FocusScope>,
        document.body,
      )
    : null;
}

export default Modal;
